// Singleton Google Maps JavaScript API loader + shared marker-icon helpers.
// Loads the official Google Maps JS SDK once (via the key in the frontend env)
// and exposes small helpers so every map on the site shares the same look.
const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let _promise = null;

export const isMapsConfigured = () => !!KEY;

// Resolve with window.google once the SDK (and requested libraries) are ready.
// Libraries requested on the first call win — subsequent calls reuse the script.
export function loadGoogleMaps(libraries = ["geometry"]) {
  if (typeof window === "undefined") return Promise.reject(new Error("no-window"));
  if (window.google && window.google.maps) return Promise.resolve(window.google);
  if (_promise) return _promise;
  if (!KEY) return Promise.reject(new Error("maps-key-missing"));
  _promise = new Promise((resolve, reject) => {
    const cb = "__ezGmapsInit";
    window[cb] = () => resolve(window.google);
    const libs = libraries && libraries.length ? `&libraries=${libraries.join(",")}` : "";
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}${libs}&callback=${cb}&v=weekly&loading=async`;
    s.async = true;
    s.defer = true;
    s.onerror = () => { _promise = null; reject(new Error("maps-script-failed")); };
    document.head.appendChild(s);
  });
  return _promise;
}

const toDataUri = (svg) =>
  "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);

// Price-tag pill marker (replaces the old Leaflet divIcon). `active` = gold/hover.
export function priceTagIcon(google, priceText, active) {
  const bg = active ? "#B8860B" : "#0A3D99";
  const fg = active ? "#0F2A5B" : "#FFFFFF";
  const fontSize = active ? 13 : 11.5;
  const text = String(priceText == null ? "—" : priceText);
  const w = Math.max(46, Math.round(text.length * fontSize * 0.64) + 24);
  const h = active ? 30 : 26;
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h + 8}' viewBox='0 0 ${w} ${h + 8}'>` +
    `<rect x='1.5' y='1.5' rx='${h / 2}' ry='${h / 2}' width='${w - 3}' height='${h - 3}' fill='${bg}' stroke='#ffffff' stroke-width='2'/>` +
    `<polygon points='${w / 2 - 6},${h - 2} ${w / 2 + 6},${h - 2} ${w / 2},${h + 6}' fill='${bg}'/>` +
    `<text x='50%' y='${h / 2}' dominant-baseline='central' text-anchor='middle' font-family='Inter,system-ui,sans-serif' font-weight='800' font-size='${fontSize}' fill='${fg}'>${text}</text>` +
    `</svg>`;
  return {
    url: toDataUri(svg),
    scaledSize: new google.maps.Size(w, h + 8),
    anchor: new google.maps.Point(w / 2, h + 6),
  };
}

// Gold star office-anchor marker.
export function officeStarIcon(google) {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='46' height='46' viewBox='0 0 46 46'>` +
    `<circle cx='23' cy='23' r='18' fill='#B8860B' stroke='#ffffff' stroke-width='3'/>` +
    `<text x='23' y='24' dominant-baseline='central' text-anchor='middle' font-family='Inter,system-ui,sans-serif' font-weight='900' font-size='21' fill='#0F2A5B'>★</text>` +
    `</svg>`;
  return {
    url: toDataUri(svg),
    scaledSize: new google.maps.Size(46, 46),
    anchor: new google.maps.Point(23, 23),
  };
}

// A single pin marker (listing-detail / single-location maps).
export function pinIcon(google, color = "#0A3D99") {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='34' height='44' viewBox='0 0 34 44'>` +
    `<path d='M17 1C8.7 1 2 7.7 2 16c0 10.5 15 27 15 27s15-16.5 15-27C32 7.7 25.3 1 17 1z' fill='${color}' stroke='#ffffff' stroke-width='2'/>` +
    `<circle cx='17' cy='16' r='5.5' fill='#ffffff'/>` +
    `</svg>`;
  return {
    url: toDataUri(svg),
    scaledSize: new google.maps.Size(34, 44),
    anchor: new google.maps.Point(17, 43),
  };
}

// Soft, calm basemap styling (keeps the clean EZtoFind feel on the roadmap view).
export const GMAP_LIGHT_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f3" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6e6e73" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "poi", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#dce8da" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#f0f0ee" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#e9e4d8" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c3d7e8" }] },
];
